import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { acceptInvite, AcceptInviteBodySchema } from '../lib/api/auth.gen';
import { useAuth } from '../lib/auth';
import { Form, FormError, FormField, FormSubmit, Input, Stack, useZodForm } from '../components/ui';

// Accept-invite page (story 2-5, W1 step 3 / W2 step 4). Public — the invitee has
// no session yet. Mirrors ResetPasswordPage (its set-password sibling): the link
// carries a single-use token; setting a password flips the membership to active,
// then we sign the invitee straight in with the password they just chose. A
// reused/expired link surfaces the backend 410 with a re-request hint.
//
// G-26 (story 2-93): the schema is a COMPOSITION over the generated contract
// schema, not a hand-declared one — `token` comes from the query string (the
// page supplies it, not the form) and `confirm` is a UI-only field the wire
// contract has no business carrying.
const acceptInviteFormSchema = AcceptInviteBodySchema.omit({ token: true })
  .extend({ confirm: z.string().min(8) })
  .refine((values) => values.password === values.confirm, {
    message: 'Passwords do not match.',
    path: ['confirm'],
  });

export function AcceptInvitePage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { login } = useAuth();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const [error, setError] = useState<string | null>(null);
  const form = useZodForm(acceptInviteFormSchema, {
    defaultValues: { password: '', confirm: '' },
  });

  const handleSubmit = async (values: z.input<typeof acceptInviteFormSchema>) => {
    setError(null);

    if (!token) {
      setError('Invite token missing. Please ask your admin to re-send the invitation.');
      return;
    }

    setStatus('loading');
    try {
      const { data } = await acceptInvite({ token, password: values.password });
      setStatus('success');
      // Sign the new member straight in with the password they just set.
      await login({ email: data.email, password: values.password });
    } catch (err) {
      const status410 = (err as { response?: { status?: number } }).response?.status === 410;
      setError(
        status410
          ? 'This invite link has already been used or has expired. Please ask your admin to re-send it.'
          : 'Unable to accept the invitation. Please ask your admin to re-send it.'
      );
      setStatus('idle');
    }
  };

  return (
    // Same deliberate `max-w-md` exception LoginPage.tsx's own header comment
    // documents — this page also renders inside PublicLayout's chrome.
    <Stack gap={8} className="mx-auto max-w-md">
      <Stack gap={3} className="text-center">
        <h1 className="text-3xl font-semibold text-foreground">Accept your invitation</h1>
        <p className="text-sm text-muted-foreground">
          Set a password to join your DefectSnap workspace.
        </p>
      </Stack>

      <Form form={form} onSubmit={handleSubmit} className="rounded-hairline border border-border bg-card space-y-4 p-6">
        <FormField name="password" label="Password" required>
          {(field) => <Input type="password" placeholder="Create a strong password" {...field} />}
        </FormField>

        <FormField name="confirm" label="Confirm password" required>
          {(field) => <Input type="password" placeholder="Repeat your password" {...field} />}
        </FormField>

        <FormError message={error} />
        {status === 'success' && (
          <p className="rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary">
            Welcome aboard — signing you in…
          </p>
        )}

        <FormSubmit className="w-full" pendingLabel="Joining…" disabled={status === 'success'}>
          {status === 'success' ? 'Joined' : 'Join workspace'}
        </FormSubmit>
      </Form>

      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to="/login" className="text-primary hover:underline">
          log in
        </Link>
        .
      </p>
    </Stack>
  );
}
